/** @file windowEvents: the viewport's window listeners (Esc's cancel-drag, frame-selection, paste). */
import { isTypingTarget } from '@zilo/pose3d-shortcuts/isTypingTarget';
import { cancelDragEvent } from '../../shell/cancelDragEvent';
import { frameEvent } from '../tree/frameEvent';
import { frameView } from './frameView';
import { pastedPng } from './pastedPng';
import { pasteImage } from './pasteImage';
import type { ViewportCtx } from './viewportCtx';

/**
 * Drops the running gizmo, IK or quad gesture: no command is sent, the override is cleared, so the drawn pose and
 * quad return to the stored ones (the pre-drag state; a released quad drag waiting on its prompt or batch stays). The pointer-up that follows finds no gesture and does nothing.
 */
function cancelEdit(ctx: ViewportCtx): void {
  if (ctx.quad.edit?.pending === false) ctx.quad.edit = null;
  if (ctx.edit === null) return;
  ctx.edit = null;
  ctx.store.getState().setPoseOverride(null);
}

/** The node a frame-selection event names (`detail.node`), else the active selected joint. */
function frameTarget(ctx: ViewportCtx, e: Event): string | undefined {
  const detail: unknown = (e as CustomEvent<unknown>).detail;
  const node = typeof detail === 'object' && detail !== null ? (detail as { node?: unknown }).node : undefined;
  return typeof node === 'string' ? node : ctx.store.getState().selection.nodes.at(-1);
}

/**
 * Ctrl+V with the pointer over the editing viewport: the browser's paste event (no key binding: Mod+V is the
 * timeline's Paste pose, one chord one meaning) puts its PNG into the selected part (`pasteImage`). Typing targets
 * and pastes elsewhere are left alone.
 */
function onPaste(ctx: ViewportCtx, e: Event): void {
  if (ctx.mode !== 'edit' || !ctx.quad.pointerIn || isTypingTarget(e.target)) return;
  e.preventDefault();
  void pasteImage(ctx, () => pastedPng(e as ClipboardEvent));
}

/**
 * `pose3d:cancel-drag` (the Esc ladder) cancels the gesture; `pose3d:frame-selection` (the tree's double-click and
 * Frame entries) frames the named node in the editing viewport; `paste` over the viewport fills the selected part
 * (`onPaste`). Returns the removal.
 */
export function windowEvents(ctx: ViewportCtx, target: EventTarget = globalThis): () => void {
  const onCancel = (): void => { cancelEdit(ctx); };
  const onFrame = (e: Event): void => { if (ctx.mode === 'edit') frameView(ctx, frameTarget(ctx, e)); };
  const paste = (e: Event): void => { onPaste(ctx, e); };
  target.addEventListener(cancelDragEvent, onCancel);
  target.addEventListener(frameEvent, onFrame);
  target.addEventListener('paste', paste);
  return () => {
    target.removeEventListener(cancelDragEvent, onCancel);
    target.removeEventListener(frameEvent, onFrame);
    target.removeEventListener('paste', paste);
  };
}
